import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { useLanguageStore } from '../stores/languageStore'
import { Plus, Search, Edit, Trash2, Eye, EyeOff } from 'lucide-react'
import Button from '../components/Button'
import Input from '../components/Input'
import LocaleToggle from '../components/LocaleToggle'

export default function Pages() {
  const [searchTerm, setSearchTerm] = useState('')
  const [showOtherLocales, setShowOtherLocales] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentLanguageId, currentLanguageCode } = useLanguageStore()

  const { data: languagesData, isLoading: languagesLoading } = useQuery({
    queryKey: ['languages'],
    queryFn: () => api.get('/languages').then((res) => res.data),
  })

  const languages = Array.isArray(languagesData?.data)
    ? languagesData.data
    : Array.isArray(languagesData)
    ? languagesData
    : []

  const currentLanguage =
    languages.length > 0
      ? languages.find((l) => l.id === currentLanguageId) || languages[0]
      : null

  const { data: pages, isLoading: pagesLoading } = useQuery({
    queryKey: ['pages', currentLanguage?.code, showOtherLocales],
    queryFn: () =>
      api
        .get('/pages', {
          params: {
            locale: currentLanguageCode || currentLanguage?.code || 'en',
            include_other_locales: showOtherLocales,
          },
        })
        .then((res) => res.data),
    enabled: !!currentLanguage,
    placeholderData: (previousData) => previousData,
  })

  const isLoading = languagesLoading || pagesLoading


  const deletePageMutation = useMutation({
    mutationFn: (id) => api.delete(`/pages/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] })
    },
  })

  const togglePublishMutation = useMutation({
    mutationFn: ({ id, status }) =>
      api.patch(`/pages/${id}/publish`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] })
    },
  })

  const filteredPages =
    pages?.data?.filter((page) =>
      page.title?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || []

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pages</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your website pages and content.
          </p>
        </div>
        <div className="flex gap-3">
          <LocaleToggle
            enabled={showOtherLocales}
            onChange={setShowOtherLocales}
          />
          <Button onClick={() => navigate('/pages/new/builder')}>
            <Plus className="mr-2 h-4 w-4" />
            New Page
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search pages..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Pages Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            All Pages
          </h3>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="text-gray-500">Loading pages...</div>
            </div>
          ) : filteredPages.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500">
                {searchTerm
                  ? 'No pages found matching your search.'
                  : 'No pages created yet.'}
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Slug
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPages.map((page) => {
                    const isMissingInCurrentLocale =
                      !page.has_translation_for_locale
                    return (
                      <tr
                        key={page.id}
                        className={
                          isMissingInCurrentLocale
                            ? 'opacity-50 bg-gray-50'
                            : ''
                        }
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div
                              className={`text-sm font-medium ${
                                isMissingInCurrentLocale
                                  ? 'text-gray-500'
                                  : 'text-gray-900'
                              }`}
                            >
                              {page.title}
                            </div>
                            {isMissingInCurrentLocale && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                                Missing Translation
                              </span>
                            )}
                          </div>
                          {page.available_locales &&
                            page.available_locales.length > 0 && (
                              <div className="flex gap-1 mt-1">
                                {page.available_locales.map((locale) => (
                                  <span
                                    key={locale}
                                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
                                  >
                                    {locale.toUpperCase()}
                                  </span>
                                ))}
                              </div>
                            )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`text-sm ${
                              isMissingInCurrentLocale
                                ? 'text-gray-400'
                                : 'text-gray-500'
                            }`}
                          >
                            {page.slug}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              page.status === 'published'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}
                          >
                            {page.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(page.updated_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            {!isMissingInCurrentLocale && (
                              <button
                                onClick={() =>
                                  togglePublishMutation.mutate({
                                    id: page.id,
                                    status:
                                      page.status === 'published'
                                        ? 'draft'
                                        : 'published',
                                  })
                                }
                                className="text-gray-400 hover:text-gray-600"
                                title={
                                  page.status === 'published'
                                    ? 'Unpublish'
                                    : 'Publish'
                                }
                              >
                                {page.status === 'published' ? (
                                  <EyeOff className="h-4 w-4" />
                                ) : (
                                  <Eye className="h-4 w-4" />
                                )}
                              </button>
                            )}
                            <button
                              onClick={() =>
                                navigate(`/pages/${page.id}/builder`)
                              }
                              className="text-gray-400 hover:text-gray-600"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                if (
                                  confirm(
                                    'Are you sure you want to delete this page?'
                                  )
                                ) {
                                  deletePageMutation.mutate(page.id)
                                }
                              }}
                              className="text-red-400 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
